import { inject, Injectable } from '@angular/core'

import { Observable } from 'rxjs'

import {
  Alert,
  AuditAction,
  AuditCategory,
  AuditLog,
  CustomSensorPrefix,
  DataQueryProps,
  DriverAssignmentType,
  TriggeredAlert,
  TriggeredAlertStatuses,
  UserProfile,
} from '@ti/contracts'
import { ExportFormat } from '@ti/core/common/contracts'
import { FleetUsersStore } from '@ti/fleet/settings/services/fleet-users.store'
import { AlertLabelsFormatter } from '@ti/shared/alerts/services'
import { ActionData } from '@ti/shared/audit/contracts'
import { AuditLogGridDataResolver } from '@ti/shared/audit/services'

@Injectable()
export class FleetAuditGridDataResolver extends AuditLogGridDataResolver {
  public readonly SUPPORTED_CATEGORIES: AuditCategory[] = [
    AuditCategory.Alerts,
    AuditCategory.Bookmarks,
    AuditCategory.CommandsManual,
    AuditCategory.CommandsAlertsBased,
    AuditCategory.Dashboard,
    AuditCategory.Dispatch,
    AuditCategory.Drivers,
    AuditCategory.Groups,
    AuditCategory.Locations,
    AuditCategory.Maintenance,
    AuditCategory.Media,
    AuditCategory.Settings,
    AuditCategory.ShareableLocationLinks,
    AuditCategory.Users,
    AuditCategory.Roles,
    AuditCategory.Vehicles,
  ]

  protected readonly alertLabelsFormatter = inject(AlertLabelsFormatter)
  protected readonly usersStore = inject(FleetUsersStore)

  public override async getActionKey(row: AuditLog) {
    let key = await super.getActionKey(row)

    if (row.category === AuditCategory.Drivers && row.action === AuditAction.DriverLinked) {
      const type: DriverAssignmentType | undefined = row.data?.type
      switch (type) {
        case DriverAssignmentType.FaceId:
          return `${key}-facial-id`
        case DriverAssignmentType.RFID:
          return `${key}-rfid`
        case DriverAssignmentType.ManualDriver:
          return `${key}-manual-driver`
        case DriverAssignmentType.ManualManager:
          return `${key}-manual-manager`
        case DriverAssignmentType.QrCode:
          return `${key}-qr-scan`
        default:
          return `${key}`
      }
    }

    if (row.category === AuditCategory.Drivers && row.action === AuditAction.DriverUnlinked) {
      const type: DriverAssignmentType | undefined = row.data?.type
      switch (type) {
        case DriverAssignmentType.FaceId:
          return `${key}-facial-id`
        case DriverAssignmentType.RFID:
          return `${key}-rfid`
        case DriverAssignmentType.ManualDriver:
          return `${key}-manual-driver`
        case DriverAssignmentType.ManualManager:
          return `${key}-manual-manager`
        default:
          return `${key}`
      }
    }

    if (
      row.category === AuditCategory.Alerts &&
      [AuditAction.Deleted, AuditAction.Modified, AuditAction.Added].includes(row.action)
    ) {
      const data = await this.getActionData(row)
      if (
        data.alertTrigger.startsWith(CustomSensorPrefix) ||
        data.alertSubTrigger.startsWith(CustomSensorPrefix)
      ) {
        key = `audit-log.actions.${row.category}.${row.action}-custom`
      }
    }
    return key
  }

  public override async getActionData(row: AuditLog): Promise<ActionData> {
    const actionData = await super.getActionData(row)

    if (
      row?.data?.alertName !== undefined &&
      row?.data?.alertTriggers !== undefined &&
      row?.data?.alertConfig !== undefined
    ) {
      try {
        const triggers = await this.alertLabelsFormatter.getAlertLabels({
          name: row.data.alertName,
          triggers: row.data.alertTriggers,
          config: row.data.alertConfig,
          isDefaultName: !!row.data.isDefaultName,
        } as Alert)
        actionData.alertTrigger = triggers.triggerName
        actionData.alertSubTrigger = triggers.subtriggerName
      } catch (e) {
        console.error(`Cannot prepare audit-log data for alert`, e)
      }
    }

    if (row?.data?.triggeredAlert) {
      try {
        const triggers = await this.alertLabelsFormatter.getTriggerAlertLabels(
          row?.data?.triggeredAlert as TriggeredAlert,
        )
        actionData.alertName = row.data.triggeredAlert.name
        actionData.alertTrigger = triggers.triggerName
        actionData.alertSubTrigger = triggers.subtriggerName
      } catch (e) {
        console.error(`Cannot prepare audit-log data connected with triggered alert`, e)
      }
    }

    if (row.category === AuditCategory.Alerts) {
      if (row.entityName) {
        actionData.alertName = row.entityName
      } else if (row.data?.alertName) {
        actionData.alertName = row.data?.alertName
      }

      const statusChangedActions = [
        AuditAction.StatusChanged,
        AuditAction.SentToDriver,
        AuditAction.ResentToDriver,
      ]
      if (statusChangedActions.includes(row.action)) {
        actionData.statusBefore = this.getTriggeredAlertStatusText(row.data.oldValue)
        actionData.statusAfter = this.getTriggeredAlertStatusText(row.data.newValue)
        const nameParts = [actionData.alertName]
        if (actionData.alertTrigger && actionData.alertTrigger !== '---') {
          nameParts.push(actionData.alertTrigger)
        }
        if (actionData.alertSubTrigger && actionData.alertSubTrigger !== '---') {
          nameParts.push(actionData.alertSubTrigger)
        }
        if (row?.data?.vehicleName && actionData.vehicleName !== '---') {
          nameParts.push(row?.data?.vehicleName)
        }
        actionData.alertName = nameParts.join(' - ')
      }
    }

    if (row.category === AuditCategory.Dispatch) {
      const newDateTime = await this.dateFormatService.formatDate(
        row.data.newDateTime,
        'grid-date-time',
      )
      const oldDateTime = await this.dateFormatService.formatDate(
        row.data.oldDateTime,
        'grid-date-time',
      )

      const changes: string[] = [row.data.changes ?? []].flat()
      actionData.routeName = row.entityName
      actionData.changes = changes
        .map((code) => this.languageService.instant('audit-log.actions.dispatch.changes.' + code))
        .join(', ')
      actionData.newDateTime = newDateTime
      actionData.oldDateTime = oldDateTime
    }

    return actionData
  }

  public override getAllUsers(): Promise<UserProfile[]> {
    const users = this.usersStore.view(undefined)
    return users.revalidateIfStale().then(() => [...users.getAll()])
  }

  public override export$(
    format: ExportFormat,
    fileName: string,
    params: DataQueryProps,
  ): Observable<void> {
    return this.api.auditLogs.export$(format, fileName, params, 'fleet')
  }

  protected getTriggeredAlertStatusText(status: TriggeredAlertStatuses) {
    const statusKey =
      status === TriggeredAlertStatuses.Unreviewed
        ? 'unreviewed'
        : status === TriggeredAlertStatuses.Reviewed
          ? 'reviewed'
          : status === TriggeredAlertStatuses.FalsePositive
            ? 'false-positive'
            : undefined
    return statusKey ? this.languageService.instant(`alerts.statuses.${statusKey}`) : '---'
  }
}
